// Contrail V3.4.0 — Admin → Simulator: demo devices that drive a route you draw.
// Positions go straight into the platform every 5–120 s (live map, alerts, events, tails, logs all work).
// History and Reports stay empty for simulators (they come from Traccar) — the Traccar route is the later upgrade.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Map as MLMap, Marker, NavigationControl, LngLatBounds, type GeoJSONSource, type MapMouseEvent } from "maplibre-gl";
import { supabase } from "../../lib/supabase";
import { adminApi, type Org } from "../../lib/admin";
import { ALL_BASEMAPS, MAP_STYLE, applyBasemap, savedBasemap } from "../../lib/basemap";
import { onStyleReady } from "../../lib/mapready";
import { ICONS, iconSvg, normIcon } from "../../lib/icons";
import { fmtDistance, metres, pathMetres, type LngLat } from "../../lib/geo";
import { dragHandle } from "../../lib/dragHandle";
import ColourPicker, { rememberColour } from "../../ui/ColourPicker";
import { Drawer, Field, Notice, Toolbar } from "./ui";

type Route = { device_id: string; path: LngLat[]; total_m: number; speed_kmh: number; speed_jitter_pct: number; mode: "loop" | "pingpong" | "once";
  dwell_s: number; interval_s: number; report_every_s?: number | null; running: boolean; start_at: string | null; last_sent_at: string | null };
type SimDev = { id: string; name: string; org_id: string; unique_id: string; icon: string | null; icon_color: string | null; tail_on: boolean;
  tail_minutes: number; status: string; sim_routes: Route | Route[] | null; device_state?: { lat: number | null; lng: number | null; speed_kn: number | null; seen_at: string | null } | null };

const MODE_LABEL = { loop: "Loop (start again from the beginning)", pingpong: "Back and forth", once: "Once, then park" } as const;
const MODE_SHORT = { loop: "Loop", pingpong: "Back & forth", once: "Once" } as const;
const routeOf = (d: SimDev) => (Array.isArray(d.sim_routes) ? d.sim_routes[0] : d.sim_routes) ?? null;
const fmtMin = (s: number) => (s < 90 ? `${Math.round(s)} s` : s < 5400 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`);
const ago = (iso: string | null) => { if (!iso) return "never"; const s = (Date.now() - Date.parse(iso)) / 1000; return s < 90 ? `${Math.round(s)} s ago` : s < 5400 ? `${Math.round(s / 60)} min ago` : `${Math.round(s / 3600)} h ago`; };

export default function Simulator({ orgs }: { orgs: Org[] }) {
  const [list, setList] = useState<SimDev[] | null>(null);
  const [edit, setEdit] = useState<SimDev | "new" | null>(null);
  const [cloneOf, setCloneOf] = useState<SimDev | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    // V3.9.0: through admin_sim_devices() — staff no longer read other companies' devices directly (Support view)
    const { data, error } = await supabase.rpc("admin_sim_devices");
    if (error) setMsg({ kind: "bad", text: error.message });
    setList((data ?? []).map((d: any) => ({ ...d, device_state: Array.isArray(d.device_state) ? d.device_state[0] ?? null : d.device_state })) as SimDev[]);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15_000); return () => clearInterval(t); }, [load]);
  const orgName = (id: string) => orgs.find((o) => o.id === id)?.name ?? "—";

  async function act(d: SimDev, what: "start" | "stop" | "delete") {
    if (what === "delete" && !confirm(`Delete the demo device ${d.name}? Its alerts, events and logs go with it.`)) return;
    setBusy(d.id);
    try {
      if (what === "delete") await adminApi("DELETE", `simulator/${d.id}`); else await adminApi("POST", `simulator/${d.id}/${what}`);
      setMsg({ kind: "ok", text: what === "start" ? `${d.name} is driving.` : what === "stop" ? `${d.name} paused.` : `Deleted ${d.name}.` });
      await load();
    } catch (e: any) { setMsg({ kind: "bad", text: e.message }); } finally { setBusy(null); }
  }

  return (
    <div className="sim">
      <Toolbar>
        <h1>Simulator</h1>
        <span className="sub">Demo devices that drive a route you draw — for demos and training.</span>
        <button className="btn-primary" onClick={() => setEdit("new")}>+ New demo device</button>
      </Toolbar>
      {msg && <Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice>}
      <p className="sim-intro">Demo devices behave like real trackers on the live map: markers move, tails, zone and speed alerts, Events and the device log all work.
        Put one in a demo company, or copy it into a client's company to show them the product — copies drive in step with the original.
        History and Reports are not recorded for demo devices yet.</p>
      <div className="atable-wrap">
        <table className="atable">
          <thead><tr><th>Demo device</th><th>Company</th><th>Route</th><th>Driving</th><th>Status</th><th /></tr></thead>
          <tbody>
            {(list ?? []).map((d) => {
              const r = routeOf(d);
              const lap = r ? r.total_m / (r.speed_kmh / 3.6) : 0;
              return (
                <tr key={d.id}>
                  <td><span className="sim-name"><i className="sim-ic" style={{ background: d.icon_color ?? "#257AF4" }} dangerouslySetInnerHTML={{ __html: iconSvg(d.icon, 14) }} />
                    <span><b>{d.name}</b><span className="sub mono">{d.unique_id}</span></span></span></td>
                  <td>{orgName(d.org_id)}</td>
                  <td>{r ? <>{fmtDistance(r.total_m)} · {r.path.length} points<div className="sub">{MODE_SHORT[r.mode]}{r.dwell_s ? ` · pause ${fmtMin(r.dwell_s)} at ends` : ""}</div></> : "—"}</td>
                  <td>{r ? <>{r.speed_kmh} km/h ±{r.speed_jitter_pct}%<div className="sub">one pass ≈ {fmtMin(lap)} · every {r.report_every_s ?? r.interval_s} s</div></> : "—"}</td>
                  <td>{r?.running ? <span className="lvl ok">Running</span> : <span className="lvl">Paused</span>}
                    <div className="sub">last fix {ago(r?.last_sent_at ?? null)}</div></td>
                  <td className="sim-act">
                    {r?.running ? <button className="btn-ghost small" disabled={busy === d.id} onClick={() => act(d, "stop")}>Pause</button>
                      : <button className="btn-ghost small" disabled={busy === d.id} onClick={() => act(d, "start")}>Start</button>}
                    <button className="btn-ghost small" onClick={() => setEdit(d)}>Edit</button>
                    <button className="btn-ghost small" onClick={() => setCloneOf(d)}>Copy to…</button>
                    <button className="btn-ghost small danger" disabled={busy === d.id} onClick={() => act(d, "delete")} aria-label={`Delete ${d.name}`}>✕</button>
                  </td>
                </tr>
              );
            })}
            {list && !list.length && <tr className="empty"><td colSpan={6}>No demo devices yet. Create one and draw its route on the map.</td></tr>}
            {!list && <tr className="empty"><td colSpan={6}>Loading…</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <SimEditor dev={edit === "new" ? null : edit} orgs={orgs} others={(list ?? []).filter((x) => edit === "new" || x.id !== edit.id)}
        onClose={() => setEdit(null)} onSaved={async (t) => { setEdit(null); setMsg({ kind: "ok", text: t }); await load(); }} />}
      {cloneOf && <CloneDrawer dev={cloneOf} orgs={orgs} onClose={() => setCloneOf(null)}
        onSaved={async (t) => { setCloneOf(null); setMsg({ kind: "ok", text: t }); await load(); }} />}
    </div>
  );
}

function CloneDrawer({ dev, orgs, onClose, onSaved }: { dev: SimDev; orgs: Org[]; onClose: () => void; onSaved: (t: string) => void }) {
  const [org, setOrg] = useState(orgs.find((o) => o.id !== dev.org_id)?.id ?? "");
  const [name, setName] = useState(dev.name);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true); setErr(null);
    try { await adminApi("POST", `simulator/${dev.id}/clone`, { org_id: org, name }); onSaved(`Copied ${dev.name} to ${orgs.find((o) => o.id === org)?.name}.`); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <Drawer title={`Copy ${dev.name}`} onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" disabled={busy || !org} onClick={save}>{busy ? "Copying…" : "Copy"}</button></>}>
      <div className="aform">
        <Field label="Copy into company" wide>
          <select value={org} onChange={(e) => setOrg(e.target.value)}>{orgs.map((o) => <option key={o.id} value={o.id}>{o.name}{o.id === dev.org_id ? " (same company)" : ""}</option>)}</select>
        </Field>
        <Field label="Name in that company" wide><input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} /></Field>
        <p className="wide sub" style={{ margin: 0 }}>The copy uses the same route and settings and drives in step with the original. Everyone in that company will see it on their map.</p>
        {err && <div className="wide error">{err}</div>}
      </div>
    </Drawer>
  );
}

// ---------------------------------------------------------------- editor
function SimEditor({ dev, orgs, others, onClose, onSaved }: { dev: SimDev | null; orgs: Org[]; others: SimDev[]; onClose: () => void; onSaved: (t: string) => void }) {
  const r0 = dev ? routeOf(dev) : null;
  const [name, setName] = useState(dev?.name ?? "");
  const [org, setOrg] = useState(dev?.org_id ?? orgs.find((o) => o.type === "internal")?.id ?? orgs[0]?.id ?? "");
  const [icon, setIcon] = useState(dev?.icon ? normIcon(dev.icon) : "car");
  const [colour, setColour] = useState<string | null>(dev?.icon_color ?? "#257AF4");
  const [path, setPath] = useState<LngLat[]>((r0?.path as LngLat[]) ?? []);
  const [speed, setSpeed] = useState(r0?.speed_kmh ?? 40);
  const [jitter, setJitter] = useState(r0?.speed_jitter_pct ?? 10);
  const [mode, setMode] = useState<Route["mode"]>(r0?.mode ?? "pingpong");
  const [dwell, setDwell] = useState(r0?.dwell_s ?? 60);
  const [interval, setInterval_] = useState(r0?.report_every_s ?? r0?.interval_s ?? 10);
  const [tail, setTail] = useState(dev?.tail_on ?? true);
  const [runNow, setRunNow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const total = useMemo(() => pathMetres(path), [path]);
  const lap = total / (Math.max(1, speed) / 3.6);

  async function save() {
    if (!name.trim()) return setErr("Give the demo device a name.");
    if (path.length < 2) return setErr("Draw the route: click at least 2 points on the map.");
    setBusy(true); setErr(null);
    const body = { name: name.trim(), org_id: org, icon, icon_color: colour, tail_on: tail, path, speed_kmh: speed, speed_jitter_pct: jitter, mode, dwell_s: dwell, interval_s: interval };
    try {
      if (dev) await adminApi("PATCH", `simulator/${dev.id}`, body); else await adminApi("POST", "simulator", { ...body, run: runNow });
      if (colour) rememberColour(colour);
      onSaved(dev ? `Saved ${name}.` : `Created ${name}${runNow ? " — it starts driving within 10 s" : ""}.`);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <Drawer title={dev ? `Demo device · ${dev.name}` : "New demo device"} onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" disabled={busy} onClick={save}>{busy ? "Saving…" : dev ? "Save" : "Create"}</button></>}>
      <div className="aform sim-form">
        <Field label="Name" wide><input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Demo · Nadi delivery van" /></Field>
        <Field label="Company" wide hint="Everyone in this company sees it on their map.">
          <select value={org} onChange={(e) => setOrg(e.target.value)}>{orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>
        </Field>
        <div className="wide">
          <RouteMap path={path} setPath={setPath} colour={colour ?? "#257AF4"} others={others} />
          <div className="sim-route-info">
            <span><b>{fmtDistance(total)}</b> · {path.length} points · one pass ≈ {fmtMin(lap)}</span>
            <span className="sim-route-btns">
              <button type="button" className="btn-ghost small" disabled={path.length < 3} onClick={() => setPath([...path, path[0]])} title="Add the first point at the end">Close the loop</button>
              <button type="button" className="btn-ghost small" disabled={path.length < 2} onClick={() => setPath([...path].reverse())}>Reverse</button>
              <button type="button" className="btn-ghost small" disabled={!path.length} onClick={() => setPath(path.slice(0, -1))}>Undo</button>
              <button type="button" className="btn-ghost small danger" disabled={!path.length} onClick={() => confirm("Clear the whole route?") && setPath([])}>Clear</button>
            </span>
          </div>
        </div>
        <Field label="Speed (km/h)"><input type="number" min={1} max={250} value={speed} onChange={(e) => setSpeed(Number(e.target.value) || 1)} /></Field>
        <Field label="Speed variation (±%)" hint="Varies the reported speed a little so it looks like real driving."><input type="number" min={0} max={50} value={jitter} onChange={(e) => setJitter(Math.max(0, Math.min(50, Number(e.target.value) || 0)))} /></Field>
        <Field label="Repeat" wide>
          <select value={mode} onChange={(e) => setMode(e.target.value as Route["mode"])}>{(Object.keys(MODE_LABEL) as Route["mode"][]).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}</select>
        </Field>
        <Field label="Pause at the end (s)" hint="Parked with ignition off — shows as Stopped."><input type="number" min={0} max={86400} value={dwell} onChange={(e) => setDwell(Math.max(0, Number(e.target.value) || 0))} /></Field>
        <Field label="Report every">
          <select value={interval} onChange={(e) => setInterval_(Number(e.target.value))}>{[5, 10, 20, 30, 60, 120].map((s) => <option key={s} value={s}>{s} seconds{s === 5 ? " (fast trackers)" : ""}</option>)}</select>
        </Field>
        <Field label="Icon" wide>
          <div className="icon-grid" role="group">
            {Object.entries(ICONS).map(([k, v]) => (
              <button key={k} type="button" className={icon === k ? "on" : ""} onClick={() => setIcon(k)} title={v.hint ? `${v.label} — ${v.hint}` : v.label}>
                <span dangerouslySetInnerHTML={{ __html: iconSvg(k, 18) }} /><small>{v.label}</small>
              </button>
            ))}
          </div>
        </Field>
        <Field label="Colour" wide><ColourPicker value={colour} onChange={setColour} allowAuto autoLabel="Status" /></Field>
        <Field label="Tail" wide><span className="check"><input type="checkbox" checked={tail} onChange={(e) => setTail(e.target.checked)} /> Show tail on the live map</span></Field>
        {!dev && <Field label="Start" wide><span className="check"><input type="checkbox" checked={runNow} onChange={(e) => setRunNow(e.target.checked)} /> Start driving as soon as it's created</span></Field>}
        {dev && r0?.running && <p className="wide sub" style={{ margin: 0 }}>Changing the route, speed or repeat mode restarts the run from the beginning.</p>}
        {err && <div className="wide error">{err}</div>}
      </div>
    </Drawer>
  );
}

/** Small map: click to add route points; drag points; "+" handles insert; click a point then Delete to remove it. */
function RouteMap({ path, setPath, colour, others }: { path: LngLat[]; setPath: (p: LngLat[]) => void; colour: string; others: SimDev[] }) {
  const el = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<MLMap | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const live = useRef(path); live.current = path;
  const handles = useRef<Marker[]>([]);
  useEffect(() => {
    if (!el.current) return;
    const first = live.current[0];
    const m = new MLMap({ container: el.current, style: MAP_STYLE, center: first ?? [177.45, -17.76], zoom: first ? 13 : 11, attributionControl: { compact: true } });
    m.addControl(new NavigationControl({ visualizePitch: false }), "top-right");
    m.doubleClickZoom.disable();
    applyBasemap(m, savedBasemap(ALL_BASEMAPS));
    onStyleReady(m, () => {
      m.addSource("sim-others", { type: "geojson", data: { type: "FeatureCollection", features: others.map((o) => { const r = routeOf(o); return r ? { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: r.path } } : null; }).filter(Boolean) as any } });
      m.addLayer({ id: "sim-others", type: "line", source: "sim-others", paint: { "line-color": "#94a3b8", "line-width": 2, "line-dasharray": [2, 2] } });
      m.addSource("sim-route", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      m.addLayer({ id: "sim-route-case", type: "line", source: "sim-route", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#fff", "line-width": 7 } });
      m.addLayer({ id: "sim-route", type: "line", source: "sim-route", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": colour, "line-width": 4 } });
      setMap(m);
      if (live.current.length > 1) { const b = new LngLatBounds(live.current[0], live.current[0]); live.current.forEach((p) => b.extend(p)); m.fitBounds(b, { padding: 40, duration: 0, maxZoom: 16 }); }
    });
    return () => { handles.current.forEach((h) => h.remove()); m.remove(); };
  }, []); // eslint-disable-line

  // V3.4.2: points drag with pointer capture (lib/dragHandle) — MapLibre's draggable markers stall while the map is busy.
  // Dragging redraws the line only; the path is saved once on release. Selecting/colour just restyle the handles.
  const paintLine = (p: LngLat[]) => {
    if (!map) return;
    (map.getSource("sim-route") as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: p.length > 1 ? [{ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: p } }] : [] });
  };
  const pointEls = useRef<HTMLElement[]>([]);
  const selRef = useRef(sel); selRef.current = sel;
  const colourRef = useRef(colour); colourRef.current = colour;
  useEffect(() => {
    if (!map) return;
    paintLine(path);
    handles.current.forEach((h) => h.remove()); handles.current = []; pointEls.current = [];
    const box = map.getContainer();
    const round = (l: { lng: number; lat: number }): LngLat => [+l.lng.toFixed(6), +l.lat.toFixed(6)];
    const mk = (cls: string, at: LngLat, opts: { preview: (base: LngLat[], p: LngLat) => LngLat[]; done?: () => void; click?: () => void; title: string }) => {
      const e = document.createElement("div"); e.className = `zh ${cls}`; e.title = opts.title; e.style.setProperty("--c", colourRef.current);
      const m = new Marker({ element: e }).setLngLat(at).addTo(map);
      let base: LngLat[] = live.current;
      dragHandle(map, m, {
        onStart: () => { base = live.current; box.classList.add("zh-dragging"); e.classList.add("dragging"); },
        onMove: (ll) => { live.current = opts.preview(base, round(ll)); paintLine(live.current); },
        onEnd: (ll) => {
          box.classList.remove("zh-dragging"); e.classList.remove("dragging");
          live.current = opts.preview(base, round(ll));
          setPath(live.current); opts.done?.();
        },
        onClick: opts.click,
      });
      handles.current.push(m);
      return e;
    };
    pointEls.current = path.map((p, i) => mk(`corner ${selRef.current === i ? "sel" : ""} ${i === 0 ? "start" : i === path.length - 1 ? "end" : ""}`, p, {
      title: i === 0 ? "Start · drag to move" : i === path.length - 1 ? "End · drag to move" : "Drag to move · click to select",
      click: () => setSel(i), preview: (b, np) => { const q = [...b]; q[i] = np; return q; } }));
    for (let i = 0; i < path.length - 1; i++) {
      const a = path[i], b2 = path[i + 1];
      if (metres(a, b2) < 15) continue;
      const mid: LngLat = [+((a[0] + b2[0]) / 2).toFixed(6), +((a[1] + b2[1]) / 2).toFixed(6)];
      mk("mid", mid, { title: "Drag or click to add a point here",
        click: () => { const q = [...live.current]; q.splice(i + 1, 0, mid); setPath(q); setSel(i + 1); },
        preview: (b, np) => { const q = [...b]; q.splice(i + 1, 0, np); return q; }, done: () => setSel(i + 1) });
    }
    return () => { box.classList.remove("zh-dragging"); };
  }, [map, path]); // eslint-disable-line
  useEffect(() => { pointEls.current.forEach((el, i) => el.classList.toggle("sel", sel === i)); }, [sel, map, path]);
  useEffect(() => {
    if (!map) return;
    if (map.getLayer("sim-route")) map.setPaintProperty("sim-route", "line-color", colour);
    handles.current.forEach((h) => h.getElement().style.setProperty("--c", colour));
  }, [map, path, colour]);

  useEffect(() => {
    if (!map) return;
    const click = (e: MapMouseEvent) => { const p: LngLat = [+e.lngLat.lng.toFixed(6), +e.lngLat.lat.toFixed(6)]; const q = [...live.current]; const at = sel != null ? sel + 1 : q.length; q.splice(at, 0, p); setPath(q); setSel(sel != null ? at : null); };
    const key = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement; if (t && /INPUT|TEXTAREA|SELECT/.test(t.tagName)) return;
      if ((e.key === "Delete" || e.key === "Backspace") && sel != null) { e.preventDefault(); setPath(live.current.filter((_, i) => i !== sel)); setSel(null); }
    };
    map.on("click", click); window.addEventListener("keydown", key);
    map.getCanvas().style.cursor = "crosshair";
    return () => { map.off("click", click); window.removeEventListener("keydown", key); };
  }, [map, sel, setPath]);

  return (
    <div className="sim-map-wrap">
      <div className="sim-map" ref={el} />
      <p className="sub">Click the map to add points along the roads. Drag points to adjust, drag or click “+” to add one in between; click a point and press Delete to remove it.
        {sel != null && <> Point {sel + 1} selected — new clicks go after it. <button type="button" className="linklike" onClick={() => setSel(null)}>Deselect</button></>}
        {others.length > 0 && " Grey dashed lines are your other demo routes."}</p>
    </div>
  );
}
